import type { Key } from "react";
import { useEffect, useState } from "react";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Field,
} from "@headlessui/react";
import { twMerge } from "tailwind-merge";

import { Label } from "~/ui";
import { Spinner } from "~/ui/Spinner";
import { CheckIcon, ChevronDownIcon } from "../Icons";

export interface SearchableSingleSelectOption<T> {
  label: string;
  id: T;
}

export interface SearchableSingleSelectProps<T> {
  id: string;
  options: SearchableSingleSelectOption<T>[];
  placeholder?: string;
  label?: string;
  value?: T | null;
  onChange: (v: T | null) => void;
  onSearch?: (query: string) => void;
  isLoading?: boolean;
  emptyMessage?: string;
}

export const SearchableSingleSelect = <T extends Key | null | undefined>({
  id,
  options,
  placeholder,
  label,
  value,
  onChange,
  onSearch,
  isLoading,
  emptyMessage,
}: SearchableSingleSelectProps<T>) => {
  const [query, setQuery] = useState("");

  const [unionOptions, setUnionOptions] =
    useState<SearchableSingleSelectOption<T>[]>(options);

  useEffect(() => {
    const newOptions = options.filter(
      (propOption) =>
        !unionOptions.some((unionOption) => unionOption.id === propOption.id),
    );
    if (newOptions.length > 0) {
      setUnionOptions((prev) => [...prev, ...newOptions]);
    }
  }, [options, unionOptions]);

  const selectedOption =
    unionOptions.find((option) => option.id === value) ?? null;

  const filteredOptions =
    query === ""
      ? options
      : options.filter((option) =>
          option.label.toLowerCase().includes(query.toLowerCase()),
        );

  return (
    <Field>
      {label && (
        <Label htmlFor={id} className={twMerge("mb-2")}>
          {label}
        </Label>
      )}

      <Combobox
        value={selectedOption}
        onChange={(option: SearchableSingleSelectOption<T> | null) => {
          onChange(option?.id ?? null);
        }}
        onClose={() => setQuery("")}
      >
        <div className="relative">
          <ComboboxInput
            id={id}
            className={twMerge(
              "w-full rounded-lg border border-neutral-variant-60 bg-surface-container-low px-4 py-3.5 text-left text-secondary-20 placeholder-neutral-variant-60 dark:border-neutral-variant-40 dark:bg-surface-container-low-dark dark:text-secondary",
            )}
            displayValue={(option: SearchableSingleSelectOption<T>) =>
              option?.label ?? ""
            }
            placeholder={placeholder}
            onChange={(event) => {
              const newQuery = event.target.value;
              setQuery(newQuery);
              onSearch?.(newQuery);
            }}
          />
          <ComboboxButton className="group absolute inset-y-0 right-2 px-2.5">
            {isLoading ? (
              <Spinner className="h-4 w-4" />
            ) : (
              <ChevronDownIcon
                className="size-4 text-secondary-20 dark:text-secondary"
                strokeWidth={3}
                aria-hidden="true"
              />
            )}
          </ComboboxButton>
        </div>

        <ComboboxOptions
          anchor="bottom"
          className={twMerge(
            "z-20 mt-3 max-h-combobox-values w-[var(--input-width)] overflow-y-auto rounded-2xl [--anchor-gap:var(--spacing-1)]",
            filteredOptions.length > 3 && "h-combobox-values",
          )}
        >
          {isLoading ? (
            <div className="flex items-center justify-center bg-surface-container-low p-8 dark:bg-surface-container-low-dark">
              <Spinner className="h-6 w-6" />
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="flex items-center justify-center bg-surface-container-low p-8 dark:bg-surface-container-low-dark">
              <p className="text-sm text-neutral-variant-60 dark:text-neutral-variant-40">
                {emptyMessage ?? "No results"}
              </p>
            </div>
          ) : (
            filteredOptions.map((option) => (
              <ComboboxOption
                key={option.id}
                value={option}
                as="div"
                className="group flex h-auto cursor-pointer select-none items-center border-b border-gray-600 bg-surface-container-low last:border-0 data-[focus]:bg-surface-container dark:border-neutral-variant-40 dark:bg-surface-container-low-dark"
              >
                <div className="flex size-full items-center gap-2 border-l-3 border-transparent p-5 pl-3 hover:border-info-80">
                  <CheckIcon
                    className={twMerge(
                      "size-4 text-neutral-variant-20 dark:text-primary-98",
                      value === option.id ? "visible" : "invisible",
                    )}
                    strokeWidth={3}
                  />
                  <div className="text-sm/6 text-neutral-variant-20 dark:text-primary-98">
                    {option.label}
                  </div>
                </div>
              </ComboboxOption>
            ))
          )}
        </ComboboxOptions>
      </Combobox>
    </Field>
  );
};
